// src/scripts/game/ui/TreasureScene.ts
import Game from "../game/Game";
import Scenes from "../enums/Scenes";
import WeaponType from "../enums/WeaponType";
import { Sizer } from "phaser3-rex-plugins/templates/ui/ui-components";
import { TEXT_STYLES } from "../../utils/FontManager";
import LevelUpItemPanel from "./LevelUpItemPanel";
import { t } from "../../utils/i18n/index";
import { DEFAULT_SCALE } from "../consts/UI";
import hapticFeedback from "../../utils/haptics";
import { LookInfo } from "../../handlers/types";
import { SPRITESHEET_KEYS, SPRITESHEET_FRAMES } from "../consts/SPRITESHEETS";

export default class TreasureScene extends Phaser.Scene {
  panel: Sizer;
  coinsAmount: number;
  upgradedWeapon?: WeaponType;
  discoveredLook?: LookInfo;
  mask: Phaser.GameObjects.Graphics;
  private scaleRatio: number;

  constructor() {
    super({ key: Scenes.UI_treasureScene });
  }

  init(data: { coins: number; weapon?: WeaponType; look?: LookInfo }) {
    this.coinsAmount = data.coins;
    this.upgradedWeapon = data.weapon;
    this.discoveredLook = data.look;
    this.scaleRatio = DEFAULT_SCALE;
  }

  create() {
    // Pause the level music
    if (Game.Core.scene.sound.get("desert_music")) {
      Game.Core.scene.sound.get("desert_music").pause();
    }

    // Play treasure sound once when treasure scene appears using pooled audio
    Game.Core.poolManager?.playTreasureMusic();

    const BASE_DEPTH = 1000;

    // Layer 1: Background mask
    this.mask = this.add.graphics().setScrollFactor(0);
    this.mask.fillStyle(0x000000, 0.8);
    this.mask.fillRect(0, 0, this.cameras.main.width, this.cameras.main.height);
    this.mask.setDepth(BASE_DEPTH);

    // Layer 2: Dialog panel
    const dialogWidth = 345 * this.scaleRatio;
    const dialogHeight = 377 * this.scaleRatio;

    // Create container for all UI elements
    const container = this.add.container(0, 0).setDepth(BASE_DEPTH + 10);

    this.panel = this.rexUI.add.sizer({
      x: this.cameras.main.width / 2,
      y: this.cameras.main.height / 2,
      width: dialogWidth,
      height: dialogHeight,
      orientation: "vertical",
      anchor: {
        centerX: "center",
        centerY: "center",
      },
      space: {
        left: 20 * this.scaleRatio,
        right: 20 * this.scaleRatio,
        top: 30 * this.scaleRatio,
        bottom: 20 * this.scaleRatio,
        item: 10 * this.scaleRatio,
      },
    });

    // Dark background with rounded corners
    this.panel.addBackground(
      this.rexUI.add
        .roundRectangle(0, 0, 0, 0, 20 * this.scaleRatio, 0x1e2938)
        .setStrokeStyle(2 * this.scaleRatio, 0x141c27)
    );

    // Title
    const title = this.add.text(0, 0, t("game.treasureFound"), {
      ...TEXT_STYLES.PIXEL,
      fontSize: `${22 * this.scaleRatio}px`,
      color: "#FFB935",
      wordWrap: { width: dialogWidth - 40 * this.scaleRatio },
    });
    this.panel.add(title, {
      padding: { top: 10 * this.scaleRatio, bottom: 20 * this.scaleRatio },
    });

    // Coins display
    const coinsContainer = this.rexUI.add.sizer({
      orientation: "horizontal",
      space: { item: 15 * this.scaleRatio },
    });

    // Start with 0 coins
    const coinsText = this.add.text(0, 0, "0", {
      ...TEXT_STYLES.PIXEL,
      fontSize: `${32 * this.scaleRatio}px`,
      color: "#FFFFFF",
    });

    const coinIcon = this.add
      .image(0, 0, SPRITESHEET_KEYS.PICKUPS, SPRITESHEET_FRAMES.PICKUPS.COIN)
      .setDisplaySize(48 * this.scaleRatio, 48 * this.scaleRatio);

    coinsContainer.add(coinsText).add(coinIcon);
    this.panel.add(coinsContainer, {
      padding: { bottom: this.discoveredLook ? 0 : 20 * this.scaleRatio },
    });

    // Secret Look section if available
    if (this.discoveredLook) {
      const lookContainer = this.rexUI.add.sizer({
        orientation: "horizontal",
        space: { item: -10 * this.scaleRatio },
      });

      const lookSprite = this.add
        .sprite(0, 0, this.discoveredLook.spriteKey)
        .setFrame(1)
        .setDisplaySize(60 * this.scaleRatio, 60 * this.scaleRatio);

      const lookText = this.add.text(0, 0, t("game.secretLook"), {
        ...TEXT_STYLES.PIXEL,
        fontSize: `${9 * this.scaleRatio}px`,
        color: "#FFA500",
        lineSpacing: 4 * this.scaleRatio,
      });

      lookContainer
        .add(lookSprite)
        .add(lookText, { padding: { top: -10 * this.scaleRatio } });
      this.panel.add(lookContainer);
    }

    // Weapon upgrade if available - create but make invisible initially
    let upgradePanel;
    if (this.upgradedWeapon) {
      const currentLevel = Game.Core.GetWeaponLevel(this.upgradedWeapon);
      upgradePanel = new LevelUpItemPanel(
        this,
        dialogWidth - 40 * this.scaleRatio,
        70 * this.scaleRatio,
        this.scaleRatio
      );
      upgradePanel.AssignData(this.upgradedWeapon, currentLevel - 1);
      upgradePanel.setAlpha(0); // Hide initially
      this.panel.add(upgradePanel);
    }

    // Add empty space that will push the button to the bottom
    this.panel.add(this.rexUI.add.sizer(), {
      proportion: 1,
    });

    // Take it all button - create but make invisible initially
    const buttonWidth = dialogWidth - 40 * this.scaleRatio;
    const buttonHeight = 49 * this.scaleRatio;

    const takeButton = this.rexUI.add
      .label({
        background: this.rexUI.add.roundRectangle(
          0,
          0,
          buttonWidth,
          buttonHeight,
          10 * this.scaleRatio,
          0x3498db
        ),
        text: this.add.text(0, 0, t("common.continue"), {
          ...TEXT_STYLES.REGULAR,
          fontSize: `${16 * this.scaleRatio}px`,
          color: "#FFFFFF",
        }),
        space: {
          left: 20 * this.scaleRatio,
          right: 20 * this.scaleRatio,
          top: 10 * this.scaleRatio,
          bottom: 10 * this.scaleRatio,
        },
        align: "center",
        width: buttonWidth,
        height: buttonHeight,
      })
      .setInteractive()
      .onClick(() => {
        // Resume level music
        if (Game.Core.scene.sound.get("desert_music")) {
          Game.Core.scene.sound.get("desert_music").resume();
        }

        Game.Core.SceneManager.ResumeFromTreasure();
      });

    // Hide the button initially
    takeButton.setAlpha(0);

    // Add button with bottom alignment
    this.panel.add(takeButton, {
      align: "bottom",
      expand: false,
      padding: { bottom: 20 * this.scaleRatio },
      proportion: 0,
    });

    // Layout everything now
    this.panel.layout();
    container.add(this.panel);

    // Animate the coin counter
    let currentCount = 0;
    const targetCount = this.coinsAmount;
    const duration = 1500; // 1.5 seconds for the animation
    const countingInterval = 20; // Update every 20ms
    const stepsCount = duration / countingInterval;
    const increment = targetCount / stepsCount;

    // Create a timer to update the count
    this.time.addEvent({
      delay: countingInterval,
      callback: () => {
        currentCount += increment;
        if (currentCount >= targetCount) {
          currentCount = targetCount;

          // Start the bouncing coins animation
          this.createBouncingCoins(BASE_DEPTH + 1);

          // Show weapon upgrade with animation if available
          if (upgradePanel) {
            this.tweens.add({
              targets: upgradePanel,
              alpha: 1,
              y: upgradePanel.y - 10,
              duration: 300,
              ease: "Back.easeOut",
            });
          }

          // Show the continue button
          takeButton.setAlpha(1);
        }

        // Add haptic feedback on each count update
        if (hapticFeedback.impactOccurred.isAvailable()) {
          hapticFeedback.impactOccurred("soft");
        }

        coinsText.setText(Math.floor(currentCount).toString());

        // Re-layout the container to adjust for the new text width
        coinsContainer.layout();
      },
      repeat: stepsCount,
    });
  }

  private createBouncingCoins(depth: number) {
    this.physics.world.setBounds(
      -200,
      -200,
      this.cameras.main.width + 400,
      this.cameras.main.height + 400
    );

    // Calculate explosion duration based on coin amount
    const explosionDuration = this.getExplosionDuration();

    // Reduce coin density from 15 to 10 coins per 50ms
    const totalCoins = Math.ceil((explosionDuration / 50) * 10);

    const coins: Phaser.Physics.Arcade.Sprite[] = [];
    const centerX = this.cameras.main.width / 2;
    const centerY = this.cameras.main.height / 2;

    // Create all coins at center
    for (let i = 0; i < totalCoins; i++) {
      const coin = this.physics.add
        .sprite(
          centerX,
          centerY,
          SPRITESHEET_KEYS.PICKUPS,
          SPRITESHEET_FRAMES.PICKUPS.COIN
        )
        .setDisplaySize(32 * this.scaleRatio, 32 * this.scaleRatio)
        .setDepth(depth);

      // Initially hide all coins
      coin.setAlpha(0);
      coins.push(coin);
    }

    // Launch coins immediately in batches
    let coinsLaunched = 0;

    // Calculate repeat count based on duration (50ms per tick)
    const repeatCount = Math.ceil(explosionDuration / 50) - 1;

    // Create a repeating timer that launches coins in batches
    const launchCoinsTimer = this.time.addEvent({
      delay: 50,
      callback: () => {
        // Reduce batch size from 15 to 10
        const batchSize = 10;

        for (let i = 0; i < batchSize; i++) {
          if (coinsLaunched >= totalCoins) {
            launchCoinsTimer.destroy();
            return;
          }

          const coin = coins[coinsLaunched];

          // Make coin visible
          coin.setAlpha(1);

          // Random angle and reduced velocity
          const angle = Phaser.Math.Between(0, 360) * (Math.PI / 180);
          const speed = Phaser.Math.Between(400, 800); // Reduced from 600-1000

          // Set velocity based on angle
          coin.setVelocity(Math.cos(angle) * speed, Math.sin(angle) * speed);

          // Add spin
          coin.setAngularVelocity(Phaser.Math.Between(-200, 200));

          coinsLaunched++;
        }
      },
      callbackScope: this,
      repeat: repeatCount,
    });

    // Schedule the bouncing phase after the explosion
    this.time.delayedCall(explosionDuration + 500, () => {
      this.createBouncingPhase(depth);
    });
  }

  // Calculate explosion duration based on treasure coin amount
  private getExplosionDuration(): number {
    const coins = this.coinsAmount;

    // Step function as specified
    if (coins < 40) return 1000;
    if (coins < 80) return 2000;
    if (coins < 200) return 3000;
    if (coins < 400) return 4000;
    if (coins < 600) return 5000;
    if (coins < 800) return 6000;
    if (coins < 1200) return 7000;
    if (coins < 1500) return 8000;
    return 9000; // 1500+ coins
  }

  // Create a separate set of coins specifically for the bouncing phase
  private createBouncingPhase(depth: number) {
    // Create 40 coins for the bouncing phase
    const bouncingCoins = 40;

    for (let i = 0; i < bouncingCoins; i++) {
      // Place coins at random positions on the screen
      const x = Phaser.Math.Between(50, this.cameras.main.width - 50);
      const y = Phaser.Math.Between(50, this.cameras.main.height - 50);

      const coin = this.physics.add
        .sprite(x, y, SPRITESHEET_KEYS.PICKUPS, SPRITESHEET_FRAMES.PICKUPS.COIN)
        .setDisplaySize(32 * this.scaleRatio, 32 * this.scaleRatio)
        .setDepth(depth);

      // Apply bouncing properties
      coin.setBounce(1);
      coin.setCollideWorldBounds(true);

      // Initial velocity for movement
      coin.setVelocity(
        Phaser.Math.Between(-150, 150),
        Phaser.Math.Between(-150, 150)
      );

      // Add some spin
      coin.setAngularVelocity(Phaser.Math.Between(-60, 60));
    }
  }
}
